import { useCallback, useEffect, useRef, useState } from 'react';
import type { DeckMode } from './types';

export interface UseDeckNavOptions {
  preview?: boolean;
  /** Autoplay interval in ms (used when autoplay is on). */
  autoplayMs?: number;
  /** Start in the autoplaying state on mount. */
  autoplayInitial?: boolean;
  /**
   * Per-slide hold in ms, one entry per slide. When supplied, autoplay
   * advances on a chained timeout using the current slide's hold instead of
   * one steady interval. Omit to keep the interval behaviour unchanged.
   */
  holdsMs?: readonly number[];
}

const DEFAULT_AUTOPLAY_MS = 8000;

type NavState = {
  idx: number;
  mode: DeckMode;
  notesOpen: boolean;
  isAutoplaying: boolean;
  autoplayMs: number;
};

type NavActions = {
  go: (n: number) => void;
  next: () => void;
  prev: () => void;
  toggleNotes: () => void;
  toggleOverview: () => void;
  toggleFullscreen: () => void;
  toggleAutoplay: () => void;
};

function readHashIndex(total: number): number {
  if (typeof window === 'undefined') return 0;
  const raw = window.location.hash.slice(1);
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.min(total - 1, parsed - 1));
}

function isTextInputTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

export function useDeckNav(total: number, options: UseDeckNavOptions = {}): NavState & NavActions {
  const { preview = false, autoplayInitial = false, holdsMs } = options;
  const autoplayMs =
    options.autoplayMs && options.autoplayMs > 0 ? options.autoplayMs : DEFAULT_AUTOPLAY_MS;
  const [idx, setIdx] = useState(() => readHashIndex(total));
  const [notesOpen, setNotesOpen] = useState(false);
  const [mode, setMode] = useState<DeckMode>('present');
  const [isAutoplaying, setIsAutoplaying] = useState(autoplayInitial);

  const idxRef = useRef(idx);
  idxRef.current = idx;

  // Held in a ref, not read from the closure: callers build this array inline
  // (DeckApp maps it off `deck.slides`), so its identity changes on every
  // render. As an effect dependency it would re-arm the timeout below on any
  // re-render during a hold — the HUD, presenter sync, a share control — and
  // a frame that kept re-rendering would never advance. A ref keeps the effect
  // keyed on `idx` alone, so the hold survives unrelated renders.
  const holdsRef = useRef(holdsMs);
  holdsRef.current = holdsMs;

  useEffect(() => {
    setIdx(readHashIndex(total));
  }, [total]);

  const go = useCallback((n: number) => {
    const next = Math.max(0, Math.min(total - 1, n));
    setIdx(next);
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `#${next + 1}`);
    }
  }, [total]);

  const next = useCallback(() => go(idxRef.current + 1), [go]);
  const prev = useCallback(() => go(idxRef.current - 1), [go]);

  const toggleNotes = useCallback(() => setNotesOpen(v => !v), []);
  const toggleOverview = useCallback(
    () => setMode(m => (m === 'overview' ? 'present' : 'overview')),
    [],
  );

  const toggleFullscreen = useCallback(() => {
    if (typeof document === 'undefined') return;
    if (!document.fullscreenElement) {
      void document.documentElement.requestFullscreen();
    } else {
      void document.exitFullscreen();
    }
  }, []);

  const toggleAutoplay = useCallback(() => setIsAutoplaying(v => !v), []);

  // Autoplay tick — wraps from last → first at a steady cadence. The functional
  // setIdx avoids stale closures, and excluding `idx` from deps keeps the
  // timer single-source-of-truth (manual nav doesn't reset it).
  //
  // A deck with per-slide holds cannot use one interval, because an interval
  // only has one duration. That case re-arms a timeout per slide, so it does
  // depend on `idx` — which also means manual nav restarts the current slide's
  // hold. Decks without `holdsMs` keep the interval, and their behaviour here
  // is byte-for-byte what it was.
  const perSlide = holdsMs != null && holdsMs.length === total;
  useEffect(() => {
    if (!isAutoplaying || total < 2 || perSlide) return;
    const handle = window.setInterval(() => {
      setIdx(prev => {
        const next = (prev + 1) % total;
        idxRef.current = next;
        window.history.replaceState(null, '', `#${next + 1}`);
        return next;
      });
    }, autoplayMs);
    return () => window.clearInterval(handle);
  }, [isAutoplaying, autoplayMs, total, perSlide]);

  useEffect(() => {
    if (!isAutoplaying || total < 2 || !perSlide) return;
    const hold = holdsRef.current?.[idx];
    const handle = window.setTimeout(() => {
      setIdx(prev => {
        const next = (prev + 1) % total;
        idxRef.current = next;
        window.history.replaceState(null, '', `#${next + 1}`);
        return next;
      });
    }, hold && hold > 0 ? hold : autoplayMs);
    return () => window.clearTimeout(handle);
  }, [isAutoplaying, total, perSlide, idx, autoplayMs]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTextInputTarget(e.target)) {
        if (e.key === 'Escape') (e.target as HTMLElement).blur();
        return;
      }
      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          next();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          prev();
          break;
        case 'Home':
          go(0);
          break;
        case 'End':
          go(total - 1);
          break;
        case 's':
        case 'S':
          toggleNotes();
          break;
        case 'o':
        case 'O':
          toggleOverview();
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          toggleAutoplay();
          break;
        case 'm':
        case 'M':
          if (preview) break;
          openPresenterMode();
          break;
        case 'e':
        case 'E':
          if (preview || !import.meta.env.DEV) break;
          openEditMode();
          break;
        case 'Escape':
          setMode('present');
          setNotesOpen(false);
          break;
        default:
          if (e.key >= '1' && e.key <= '9') {
            go(Number.parseInt(e.key, 10) - 1);
          }
      }
    }
    window.addEventListener('keydown', onKey);

    function onCrossFrame(e: MessageEvent) {
      if (!preview) return;
      if (e.data?.type !== 'sn-nav') return;
      if (e.data.dir === 'next') next();
      else if (e.data.dir === 'prev') prev();
      else if (typeof e.data.idx === 'number') go(e.data.idx);
    }
    if (preview) window.addEventListener('message', onCrossFrame);

    return () => {
      window.removeEventListener('keydown', onKey);
      if (preview) window.removeEventListener('message', onCrossFrame);
    };
  }, [next, prev, go, toggleNotes, toggleOverview, toggleFullscreen, toggleAutoplay, total, preview]);

  return {
    idx,
    mode,
    notesOpen,
    isAutoplaying,
    autoplayMs,
    go,
    next,
    prev,
    toggleNotes,
    toggleOverview,
    toggleFullscreen,
    toggleAutoplay,
  };
}

function openEditMode(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  params.set('edit', '1');
  const url = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
  window.history.pushState(null, '', url);
  window.dispatchEvent(new Event('sn-route'));
}

function openPresenterMode(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  params.set('presenter', '1');
  const url = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
  window.history.pushState(null, '', url);
  window.dispatchEvent(new Event('sn-route'));
}
